import { makePrice, Price } from 'lib/core';
import { TruckIcon } from './TruckIcon';
import {
  getCurrentTierIndex,
  getDeliveryProgressInfo,
  getDeliveryTierProgressPercent,
} from './utils';
import { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { DELIVERY_FEE_TIERS } from './constants';
import { AnimatedNumber, Confetti } from 'components/common/v2';

interface CompactDeliveryProgressProps {
  total: number;
}

export const CompactDeliveryProgress = ({
  total,
}: CompactDeliveryProgressProps) => {
  const { amountAway, nextFee, currentFee } = getDeliveryProgressInfo(
    total,
  );
  const percent = getDeliveryTierProgressPercent(total);

  const [displayPercent, setDisplayPercent] = useState(percent);
  const [animate, setAnimate] = useState(true);

  const previous = useRef({
    total,
    tier: getCurrentTierIndex(total),
  });

  const [showConfetti, setShowConfetti] = useState(false);
  const previousFee = useRef(currentFee);

  useEffect(() => {
    const becameFree = previousFee.current !== 0 && currentFee === 0;
    previousFee.current = currentFee;

    if (!becameFree) return;

    setShowConfetti(true);

    const timeout = window.setTimeout(() => {
      setShowConfetti(false);
    }, 1000);

    return () => window.clearTimeout(timeout);
  }, [currentFee]);

  useEffect(() => {
    const currentTier = getCurrentTierIndex(total);

    const previousTotal = previous.current.total;
    const previousTier = previous.current.tier;

    const isIncreasing = total > previousTotal;
    const isDecreasing = total < previousTotal;

    const crossedForward = isIncreasing && currentTier > previousTier;
    const crossedBackward =
      isDecreasing && currentTier < previousTier;

    const isEnteringMaxTier =
      crossedForward && currentTier === DELIVERY_FEE_TIERS.length - 1;
    const isLeavingMaxTier =
      crossedBackward &&
      previousTier === DELIVERY_FEE_TIERS.length - 1;

    previous.current = {
      total,
      tier: currentTier,
    };

    if (!crossedForward && !crossedBackward) {
      setAnimate(true);
      setDisplayPercent(percent);
      return;
    }

    if (isEnteringMaxTier) {
      setAnimate(true);
      setDisplayPercent(100);
      return;
    }

    if (isLeavingMaxTier) {
      setAnimate(true);
      setDisplayPercent(percent);
      return;
    }

    if (isLeavingMaxTier) {
      setAnimate(true);
      setDisplayPercent(percent);
      return;
    }

    if (crossedForward) {
      setAnimate(true);
      setDisplayPercent(100);

      const timeout = setTimeout(() => {
        setAnimate(false);
        setDisplayPercent(0);

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setAnimate(true);
            setDisplayPercent(percent);
          });
        });
      }, 1000);

      return () => clearTimeout(timeout);
    }

    setAnimate(true);
    setDisplayPercent(0);

    const timeout = setTimeout(() => {
      setAnimate(false);
      setDisplayPercent(100);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setAnimate(true);
          setDisplayPercent(percent);
        });
      });
    }, 1000);

    return () => clearTimeout(timeout);
  }, [percent, total]);

  return (
    <div className="tw-flex tw-items-center tw-gap-2.5 tw-px-4 tw-py-2.5">
      <span className="tw-flex tw-w-8 tw-h-8 tw-flex-none tw-items-center tw-justify-center tw-rounded-full tw-bg-primary">
        <TruckIcon className="tw-h-5 tw-w-5 tw-text-white" />
      </span>
      <div className="tw-mb-0">
        {amountAway && amountAway > 0 ? (
          <>
            You're just{' '}
            <strong className="tw-text-primary">
              <AnimatedNumber value={amountAway} format={makePrice} />
            </strong>{' '}
            away from{' '}
            <strong className="tw-text-primary">
              {nextFee === 0 ? (
                <strong>FREE</strong>
              ) : (
                <Price value={nextFee} />
              )}
            </strong>{' '}
            delivery!
          </>
        ) : (
          <>
            Steady, lah! Your delivery is now{' '}
            <span className="tw-font-therebrat tw-text-primary tw-tracking-wide tw-relative">
              FREE!
              <Confetti active={showConfetti} />
            </span>
          </>
        )}
      </div>
      <div className="tw-flex tw-items-center tw-gap-2 tw-ml-auto">
        {(currentFee ?? 0) > 0 && (
          <strong className="tw-text-[#333] tw-text-[12px] tw-transition-all tw-duration-300 tw-ease-in-out">
            Delivery: <Price value={currentFee} />
          </strong>
        )}
        <div
          className={cn(
            'tw-h-2 tw-w-80 tw-rounded-full tw-bg-white-200 tw-origin-right tw-transition-all tw-duration-300 tw-ease-in-out',
            (currentFee ?? 0) === 0
              ? 'tw-translate-x-4 tw-opacity-0 tw-pointer-events-none'
              : 'tw-translate-x-0 tw-opacity-100',
          )}
        >
          <div
            className={cn('tw-h-2 tw-rounded-full tw-bg-primary')}
            style={{
              width: `${displayPercent}%`,
              transition: animate
                ? 'width 1000ms ease-in-out'
                : 'none',
            }}
          />
        </div>
      </div>
    </div>
  );
};
